import { ITEMS, getOrigin, getPort } from '@realseas/shared';
import type { CaptainSave, Item, ItemCategory } from '@realseas/shared';
import type { Connection } from '../Connection.js';
import { setHTML, esc } from '../dom.js';

const CATEGORY_ORDER: ItemCategory[] = [
  'navigation',
  'crew',
  'provisions',
  'antiscorbutics',
  'fishing',
  'armaments',
  'misc',
];

const CATEGORY_TITLES: Record<ItemCategory, { title: string; lede: string }> = {
  navigation:     { title: 'Navigation',     lede: 'without it you are lost' },
  crew:           { title: 'Crew',           lede: 'the men who will sail or drown with you' },
  provisions:     { title: 'Provisions',     lede: 'bread, meat, and water' },
  antiscorbutics: { title: 'Antiscorbutics', lede: 'against scurvy' },
  fishing:        { title: 'Fishing',        lede: 'rod, net, harpoon — for what the sea will give' },
  armaments:      { title: 'Armaments',      lede: 'cannons, powder, and steel' },
  misc:           { title: 'Ship Stores & Sundries', lede: 'small things that may save you' },
};

// Recommended loadouts keyed by originId. Ordered: essentials first, then quality-of-life.
// Quantities are targets — the handler buys only the delta (recommended - already owned).
// Loadouts must fit each origin's starting reales. Hand-balanced; the totals
// below leave a small margin so the player can tweak before setting sail.
//   Buccaneer: 500 reales (no carpenter — too expensive — fish for food instead)
//   Sailor:    1,500 reales
//   Noble:     5,000 reales
const RECOMMENDED: Record<string, Array<[string, number]>> = {
  // Buccaneer 500 → 475: relies on the rod for food, no carpenter or lumber.
  buccaneer: [
    ['compass', 1], ['sailor', 3],
    ['fresh_water', 40], ['hardtack', 50], ['lemons', 9],
    ['fishing_rod', 1], ['ships_cat', 1], ['rum', 10],
  ],
  // Sailor 1,500 → 1,460: surgeon + medicine + carpenter + 3 lumber bundles.
  sailor: [
    ['compass', 1], ['sextant', 1],
    ['ship_surgeon', 1], ['medicine_chest', 1], ['carpenters_tools', 1], ['lumber', 3],
    ['sailor', 5],
    ['fresh_water', 60], ['hardtack', 50], ['lemons', 10], ['rum', 18],
    ['ships_cat', 1],
  ],
  // Noble 5,000 → 4,964: full kit, including 2 cannons, 6 lumber, deep antiscurvy.
  noble: [
    ['compass', 1], ['telescope', 1], ['sextant', 1], ['sea_charts', 1],
    ['ship_surgeon', 1], ['medicine_chest', 1], ['master_gunner', 1], ['carpenters_tools', 1],
    ['sailor', 14], ['lumber', 6],
    ['fresh_water', 200], ['hardtack', 150], ['salted_pork', 100], ['dried_beans', 80],
    ['lemons', 50], ['lime_juice', 10], ['rum', 55],
    ['fishing_rod', 1], ['fishing_net', 3], ['harpoon', 1], ['bait', 10],
    ['spare_sailcloth', 3], ['spare_cordage', 3], ['ships_cat', 1],
    ['cannon_6lb', 2], ['gunpowder', 10], ['round_shot', 20], ['grape_shot', 5],
    ['cutlass', 8], ['musket', 4], ['musket_balls', 5], ['pistol', 2],
  ],
};

// Module state for one-shot animations across re-renders
let lastAction: { itemId: string; kind: 'buy' | 'sell' } | null = null;
let lastReales: number | null = null;
let screenReady = false;
let currentCaptain: CaptainSave | null = null;

/** Clear all module-level state. Call when navigating away from outfitting so
 *  a stale "screenReady" flag can't make the next outfitting visit skip setHTML. */
export function resetOutfittingState(): void {
  lastAction = null;
  lastReales = null;
  screenReady = false;
  currentCaptain = null;
}

function ownedOf(captain: CaptainSave, itemId: string): number {
  return captain.inventory.find(i => i.itemId === itemId)?.quantity ?? 0;
}

function renderItemRow(item: Item, captain: CaptainSave): string {
  const owned = ownedOf(captain, item.id);
  const canAfford = captain.reales >= item.price;
  const uniqueOwned = item.unique && owned >= 1;
  const buyDisabled = uniqueOwned || !canAfford;
  const sellDisabled = owned <= 0;

  const classes = ['item-row'];
  if (uniqueOwned) classes.push('unique-owned');
  if (item.essential) classes.push('essential');

  const qtyControl = item.unique
    ? ''
    : `<input type="number" min="1" value="1" data-qty="${esc(item.id)}" />`;

  const essentialMark = item.essential ? `<span class="essential-mark">★</span>` : '';

  return `
    <div class="${classes.join(' ')}" data-row="${esc(item.id)}">
      <div>
        <div>
          <span class="name">${esc(item.name)}</span>${essentialMark}
          <span class="price">${item.price.toLocaleString()} reales</span>
          <span class="unit">per ${esc(item.unit)}</span>
        </div>
        <div class="desc">${esc(item.description)}</div>
      </div>
      <div class="controls">
        <span class="owned">${owned > 0 ? `× ${owned}` : ''}</span>
        ${qtyControl}
        <button data-buy="${esc(item.id)}" ${buyDisabled ? 'disabled' : ''}>Buy</button>
        <button class="secondary" data-sell="${esc(item.id)}" ${sellDisabled ? 'disabled' : ''}>Sell</button>
      </div>
    </div>
  `;
}

function renderCategory(cat: ItemCategory, captain: CaptainSave): string {
  const items = ITEMS.filter(i => i.category === cat && !i.notForSale);
  if (items.length === 0) return '';
  const { title, lede } = CATEGORY_TITLES[cat];
  return `
    <section class="category-section">
      <h2>${esc(title)}<span class="lede">— ${esc(lede)}</span></h2>
      <div class="item-list">
        ${items.map(i => renderItemRow(i, captain)).join('')}
      </div>
    </section>
  `;
}

// Surgical DOM patch — updates only the parts that change on buy/sell.
// Preserves qty input values and avoids re-triggering entry animations.
function patchOutfittingDom(container: HTMLElement, captain: CaptainSave): void {
  currentCaptain = captain;
  const purseEl = container.querySelector<HTMLElement>('.purse');
  if (purseEl) {
    const purseChanged = lastReales !== null && lastReales !== captain.reales;
    lastReales = captain.reales;
    purseEl.textContent = `Purse: ${captain.reales.toLocaleString()} reales`;
    if (purseChanged) {
      purseEl.classList.remove('bumped');
      void purseEl.offsetWidth;
      purseEl.classList.add('bumped');
    }
  }

  for (const item of ITEMS.filter(i => !i.notForSale)) {
    const row = container.querySelector<HTMLElement>(`[data-row="${item.id}"]`);
    if (!row) continue;

    const owned = ownedOf(captain, item.id);
    const canAfford = captain.reales >= item.price;
    const uniqueOwned = !!item.unique && owned >= 1;

    row.classList.toggle('unique-owned', uniqueOwned);

    const ownedEl = row.querySelector('.owned');
    if (ownedEl) ownedEl.textContent = owned > 0 ? `× ${owned}` : '';

    const buyBtn = row.querySelector<HTMLButtonElement>('[data-buy]');
    if (buyBtn) buyBtn.disabled = uniqueOwned || !canAfford;

    const sellBtn = row.querySelector<HTMLButtonElement>('[data-sell]');
    if (sellBtn) sellBtn.disabled = owned <= 0;

    if (lastAction?.itemId === item.id) {
      const cls = lastAction.kind === 'buy' ? 'flash-buy' : 'flash-sell';
      row.classList.remove(cls);
      void row.offsetWidth;
      row.classList.add(cls);
    }
  }

  lastAction = null;
}

export function renderOutfittingScreen(
  container: HTMLElement,
  conn: Connection,
  captain: CaptainSave,
): void {
  const origin = captain.originId ? getOrigin(captain.originId) : null;
  const port = captain.portId ? getPort(captain.portId) : null;
  const portName = port?.name ?? 'Cádiz';
  const destination = captain.destinationId ? getPort(captain.destinationId) : null;
  const returning = captain.voyagesCompleted > 0;

  // After first render, patch the DOM in-place so qty inputs and animations are preserved.
  if (screenReady && container.querySelector('.outfit-header')) {
    patchOutfittingDom(container, captain);
    return;
  }

  lastReales = captain.reales;
  currentCaptain = captain;
  screenReady = true;

  setHTML(container, `
    <div class="outfit-header">
      <h1>The ${esc(portName)} Dockside</h1>
      <p class="subtitle" style="text-align: left; margin: 0;">
        Captain ${esc(captain.username)}${origin ? ` — ${esc(origin.name)}` : ''}${destination ? ` — bound for ${esc(destination.name)}` : ''}
      </p>
    </div>

    <p class="outfit-warning">
      ${returning
        ? `Sell what the crossing brought in — fish, whale meat, salvage — and refit for the road ahead. Once you set sail, you cannot return to ${esc(portName)}.`
        : `Once you set sail, you cannot return to ${esc(portName)}. Spend wisely.`}
      The dockmaster buys back at half price.
    </p>

    <div class="outfit-toolbar">
      <div class="toolbar-label">Captain ${esc(captain.username)}</div>
      <div class="purse">Purse: ${captain.reales.toLocaleString()} reales</div>
      <button id="buy-recommended-btn">Buy Recommended</button>
    </div>

    ${CATEGORY_ORDER.map(c => renderCategory(c, captain)).join('')}

    <div class="set-sail-bar">
      <div style="color: var(--ink-soft); font-style: italic;">
        When you are ready, the wind will not wait.
      </div>
      <button class="primary" id="set-sail-btn">Set Sail</button>
    </div>
  `);

  container.querySelector<HTMLButtonElement>('#buy-recommended-btn')!.onclick = () => {
    const c = currentCaptain!;
    const loadout = RECOMMENDED[c.originId ?? 'buccaneer'] ?? RECOMMENDED.buccaneer;
    for (const [itemId, qty] of loadout) {
      const delta = Math.max(0, qty - ownedOf(c, itemId));
      if (delta > 0) conn.send({ type: 'buy', itemId, quantity: delta });
    }
  };

  container.querySelectorAll<HTMLButtonElement>('[data-buy]').forEach(btn => {
    btn.onclick = () => {
      const itemId = btn.dataset.buy!;
      const qtyEl = container.querySelector<HTMLInputElement>(`[data-qty="${itemId}"]`);
      const qty = qtyEl ? Math.max(1, parseInt(qtyEl.value, 10) || 1) : 1;
      lastAction = { itemId, kind: 'buy' };
      conn.send({ type: 'buy', itemId, quantity: qty });
    };
  });

  container.querySelectorAll<HTMLButtonElement>('[data-sell]').forEach(btn => {
    btn.onclick = () => {
      const itemId = btn.dataset.sell!;
      const qtyEl = container.querySelector<HTMLInputElement>(`[data-qty="${itemId}"]`);
      const qty = qtyEl ? Math.max(1, parseInt(qtyEl.value, 10) || 1) : 1;
      lastAction = { itemId, kind: 'sell' };
      conn.send({ type: 'sell', itemId, quantity: qty });
    };
  });

  container.querySelector<HTMLButtonElement>('#set-sail-btn')!.onclick = () => {
    const c = currentCaptain!;
    const warnings: string[] = [];
    if (ownedOf(c, 'compass') < 1) warnings.push('You have no compass.');
    if (ownedOf(c, 'sailor') < 1) warnings.push('You have no crew.');
    if (totalFood(c) <= 0) warnings.push('You have no food.');
    if (ownedOf(c, 'fresh_water') < 1) warnings.push('You have no fresh water.');
    if (!hasAnyAntiscorbutic(c)) warnings.push('You have no antiscorbutics — your crew will get scurvy.');

    const portName = c.portId ? (getPort(c.portId)?.name ?? 'this port') : 'this port';
    const msg = warnings.length === 0
      ? `Once you cast off, there is no coming back to ${portName}. Are you ready?`
      : `Some matters are not in order:\n\n  • ${warnings.join('\n  • ')}\n\nSet sail anyway?`;

    if (confirm(msg)) {
      // Reset module state so it doesn't leak into the next session
      lastAction = null;
      lastReales = null;
      currentCaptain = null;
      screenReady = false;
      conn.send({ type: 'set_sail' });
    }
  };
}

function totalFood(captain: CaptainSave): number {
  const foodIds = ['hardtack', 'salted_pork', 'dried_beans'];
  return foodIds.reduce((sum, id) => sum + ownedOf(captain, id), 0);
}

function hasAnyAntiscorbutic(captain: CaptainSave): boolean {
  return ['lemons', 'sauerkraut', 'lime_juice'].some(id => ownedOf(captain, id) > 0);
}
